<!--
  - Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
  - This is CONFIDENTIAL code. Use is subject to license terms.
  -->
<template>
  <div>
    <div class="d-flex flex-nowrap justify-content-between align-items-baseline">
      <!-- Property Name -->
      <label v-if="nameAsLabel" class="mr-2" v-html="name"></label>
      <text-input
        v-else
        :id="attributeId"
        :value="name"
        :disabled="disabled"
        type="text"
        encoding="none"
        autocomplete="off"
        class="flex-grow-1 mr-2"
        @blur="$emit('updateAttributeName', $event.target.value)"
      ></text-input>

      <!-- Text or Number value -->
      <text-input
        v-if="type !== 'BOOLEAN'"
        :id="attributeId"
        :value="typeSafeValue"
        :type="type === 'STRING' ? 'text' : 'number'"
        :class="{ 'align-right': alignRight, 'min-w-50': !alignRight }"
        :input-class="alignRight ? 'disable-scroll text-right' : ''"
        :disabled="disabled"
        encoding="none"
        autocomplete="off"
        @input="processAttributeInput"
      ></text-input>

      <!-- Boolean value -->
      <div v-else :class="['mt-2', { 'min-w-25': alignRight, 'text-center': alignRight, 'min-w-50': !alignRight }]">
        <toggle-button
          :id="attributeId"
          :value="typeSafeValue"
          :sync="true"
          :disabled="disabled"
          @input="processAttributeInput"
        ></toggle-button>
      </div>
      <slot name="after-attribute-region"></slot>
    </div>
  </div>
</template>

<script>
import TextInput from "@/components/TextInput"
import { v4 as UUIDv4 } from "uuid"
import { castValueToType } from "@/utils/TypeCastHelpers"

/**
 * Wraps logic to render a given value based on the provided type. I.e. when given boolean as a type
 * a toggle buttton is rendered.
 *
 * @event updateAttributeName - containing the string defining the new attribute name
 * @event updateAttributeValue - containing the value of the input based on {@see type}
 */
export default {
  name: "AdjustableTypeInput",

  components: { TextInput },

  props: {
    /**
     * Defines the id used for the html
     * element rendered, as such must be unique
     */
    id: { type: String, required: true },

    /**
     * Defines the name of the field
     * used to describe input
     */
    name: { type: String, required: true },

    /**
     * Defines the value driving
     * the html element
     */
    value: { type: [String, Number, Boolean], required: false },

    /**
     * Defines both the type to which
     * the value will be casted as well
     * as which html element will be used
     * to represent the value
     *
     * @values NUMBER, STRING, BOOLEAN
     */
    type: { type: "NUMBER" | "STRING" | "BOOLEAN", required: true },

    /**
     * Defines if the field used to
     * describe the input can also be
     * modified.
     */
    nameEditable: { type: Boolean, default: true },

    /**
     * Defines if the field used to
     * describe the input is rendered
     * as a label. By default the field
     * is rendered as an input
     */
    nameAsLabel: { type: Boolean, default: false },

    /**
     * Defines if the this input is
     * disabled and therefore editable
     */
    disabled: { type: Boolean, default: false },

    /**
     * Defines whether the html element
     * used to render the value is align
     * left. By default it is align next
     * to the name
     */
    alignRight: { type: Boolean, default: false },
  },

  computed: {
    /**
     * Custom generated copy of the id provided
     */
    attributeId() {
      let seed = this.id + "--attribute"

      // If there exists a DOM element already using the given ID base,
      // return a randomized construct
      if (document.getElementById(seed)) {
        seed = this.id + UUIDv4() + "--attribute"
      }

      return seed
    },

    /**
     * Generates a type safe copy of the value given. Particularly important for
     * html elements that expect a specific value type
     */
    typeSafeValue() {
      // Due to the multi-value aspect of this component, assume that casting is desired
      // implicitly. Only when a cast cannot be performed will an error be thrown.
      return castValueToType(this.value, this.type)
    },
  },

  methods: {
    processAttributeInput(val) {
      // Due to the input field seeming to not ensure type safety force cast the
      // input value to the correct type to ensure consumers don't need to ensure
      // type compatibility
      this.$emit("updateAttributeValue", castValueToType(val, this.type))
    },
  },

  i18n: {
    messages: {
      de: {},
      en: {},
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss_custom/custom-variables.scss";

.dropdown-type-switch {
  min-width: 0;
}

.min-w-50 {
  min-width: 50%;
}

.min-w-25 {
  min-width: 25%;
}

.align-right {
  margin-right: 1.8rem;
  width: 12%;
}
</style>
